import React from 'react'
import { IconThumbUp, IconThumbDown } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { formatSummaryTableContent, formatTime } from '@/utils/helpers'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'

export const FeatureFeedbackTable = ({ loading, rows = [] }) => {
  const columns = [
    {
      accessor: 'sentiment',
      label: 'Feedback',
      isSortable: true,
      isSearchable: true,
      searchSchema: {
        label: 'Feedback',
        type: 'select',
        options: [
          { label: 'Positive', value: true },
          { label: 'Negative', value: false },
        ],
      },
      format: (value) => {
        return (
          <div className="flex-align-center small-gap">
            {value === true ? (
              <Pill success outlined icon={<IconThumbUp />} />
            ) : value === false ? (
              <Pill critical outlined icon={<IconThumbDown />} />
            ) : null}
          </div>
        )
      },
    },
    {
      accessor: 'call_id',
      label: 'Call ID',
      isSearchable: true,
      isCopiable: true,
      collapsing: true,
      format: (id) => {
        if (!id) return null
        return (
          <Link to={`/calls/${id}`} className="table-link">
            {id}
          </Link>
        )
      },
    },
    {
      accessor: 'created_at',
      label: 'Created On',
      collapsing: true,
      format: (value) => formatTime(value),
      noWrap: true,
    },
    {
      accessor: 'username',
      label: 'User',
      isSearchable: true,
      collapsing: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Users',
      },
    },
    {
      accessor: 'organization_name',
      label: 'Organization',
      isSearchable: true,
      collapsing: true,
      noWrap: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Organizations',
      },
    },
    {
      accessor: 'feature',
      label: 'Feature',
      isSearchable: true,
      collapsing: true,
      noWrap: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Features',
      },
    },
    {
      accessor: 'category',
      label: 'Category',
      isSearchable: true,
      collapsing: true,
      searchSchema: {
        type: 'multiSelect',
        label: 'Categories',
      },
    },
    {
      accessor: 'comment',
      label: 'Comment',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'question',
      label: 'Question',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'answer',
      label: 'Answer',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'context',
      label: 'Playbook Context',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'headline',
      label: 'Summary Headline',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'content',
      label: 'Summary Content',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'ai_agent_guidance',
      label: 'AI Agent Guidance',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'checklist_item',
      label: 'Checklist Item',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'salesforce_data_included',
      label: 'Salesforce Data',
      isSearchable: true,
      collapsing: true,
      searchSchema: {
        label: 'Salesforce Data',
        type: 'select',
        options: [
          { label: 'Included', value: true },
          { label: 'Not Included', value: false },
        ],
      },
      format: (value) => {
        if (value === true) {
          return 'Included'
        }
        if (value === false) {
          return 'Not Included'
        }
        return null
      },
    },
    {
      accessor: 'customer_highlights',
      label: 'Customer Highlights',
      isSearchable: true,
      isCopiable: true,
      format: (value) => {
        if (value) {
          return <div>{formatSummaryTableContent(value)}</div>
        }
        return null
      },
    },
    {
      accessor: 'prior_call_ids',
      label: 'Prior Call IDs',
      isSearchable: true,
      format: (value) => {
        if (isEmpty(value)) {
          return null
        }
        return (
          <div>
            {value.map((id, index) => (
              <React.Fragment key={id}>
                {index > 0 && ', '}
                <Link to={`/calls/${id}`} className="table-link">
                  {id}
                </Link>
              </React.Fragment>
            ))}
          </div>
        )
      },
    },
  ]

  return (
    <Card className="no-padding">
      <AdvancedTable
        loading={loading}
        rows={rows}
        columns={columns}
        pagination
        rowsPerPage={10}
        wrapColumnContent={false}
        defaultOrder="desc"
        defaultOrderBy="created_at"
      />
    </Card>
  )
}
